import type { ReactNode } from 'react';

import { I18nextProvider } from 'react-i18next';

import { AuthProvider } from '@/shared/auth/AuthContext';
import { i18n, LocaleProvider } from '@/shared/i18n';
import { PermissionProvider } from '@/shared/permissions/PermissionContext';
import { SessionProvider } from '@/shared/session';
import { TenantProvider } from '@/shared/tenant/TenantContext';

import { QueryProvider } from './QueryProvider';
import { RouterProvider } from './RouterProvider';
import { ThemeProvider } from './ThemeProvider';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * Composes every application-wide provider in a single place.
 *
 * Order (outermost → innermost):
 *  I18next → Locale → Theme → Query → Session → Auth → Tenant → Permission → Router
 *
 * I18nextProvider + LocaleProvider sit outermost so the rest of the tree (including
 * ThemeProvider, which reads the locale for MUI locale adapters) can access them.
 * Session sits inside Query (it bootstraps from `GET /api/v1/me` via TanStack
 * Query) and feeds the auth/tenant/permission projections beneath it.
 * Authentication wraps tenancy (a user may access several tenants), and
 * permissions sit closest to the routed UI since they are tenant-specific.
 * Routing is innermost so routed components can read every context above it.
 */
export function AppProviders({ children }: AppProvidersProps) {
  return (
    <I18nextProvider i18n={i18n}>
      <LocaleProvider>
        <ThemeProvider>
          <QueryProvider>
            <SessionProvider>
              <AuthProvider>
                <TenantProvider>
                  <PermissionProvider>
                    <RouterProvider>{children}</RouterProvider>
                  </PermissionProvider>
                </TenantProvider>
              </AuthProvider>
            </SessionProvider>
          </QueryProvider>
        </ThemeProvider>
      </LocaleProvider>
    </I18nextProvider>
  );
}
